import React from 'react';
import { t } from '../../utils/translations';

const getQuestionType = (question) => {
  if (question.question_type) return question.question_type;
  if (Array.isArray(question.correct_answers)) return 'multiple_choice';
  if (!Array.isArray(question.options) || question.options.length === 0) return 'text';
  return 'single_choice';
};

const QuizTab = ({
  quiz,
  subjects = [],
  completedQuizzes = [],
  loadingQuizHistory = false,
  activeQuizzes = [],
  loadingActiveQuizzes = false,
  openingActiveQuiz = false,
  answers,
  loading,
  generatingQuiz,
  loadingQuizReview = false,
  result,
  userLanguage,
  fullscreen = false,
  onClose,
  onCancelQuiz,
  onSubmit,
  onGenerateNewQuiz,
  onAddSectionForSubject,
  onOpenQuizHistoryAttempt,
  onOpenActiveQuiz,
  onLoadSections,
  onAnswerChange,
  initialOpenPanel = null,
  onInitialOpenPanelApplied
}) => {
  const [subjectId, setSubjectId] = React.useState('');
  const [quizSource, setQuizSource] = React.useState('auto');
  const [questionCount, setQuestionCount] = React.useState(6);
  const [availableSections, setAvailableSections] = React.useState([]);
  const [selectedSectionIds, setSelectedSectionIds] = React.useState([]);
  const [loadingSections, setLoadingSections] = React.useState(false);
  const [openPanel, setOpenPanel] = React.useState(null);
  const [reviewModalData, setReviewModalData] = React.useState(null);
  const [openingHistoryAttemptId, setOpeningHistoryAttemptId] = React.useState(null);
  const showFullscreenGenerating = fullscreen && generatingQuiz && !quiz;

  React.useEffect(() => {
    if (!subjectId && subjects.length > 0) {
      setSubjectId(String(subjects[0].id));
    }
  }, [subjectId, subjects]);

  React.useEffect(() => {
    const loadSections = async () => {
      if (quizSource !== 'today_sections' || !subjectId || !onLoadSections) {
        return;
      }
      setLoadingSections(true);
      const sections = await onLoadSections(Number(subjectId));
      setAvailableSections(sections);
      setSelectedSectionIds(sections.map((section) => section.id));
      setLoadingSections(false);
    };
    loadSections();
  }, [quizSource, subjectId, onLoadSections]);

  React.useEffect(() => {
    if (openPanel) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [openPanel]);

  React.useEffect(() => {
    if (fullscreen || !initialOpenPanel) return;
    setOpenPanel(initialOpenPanel);
    if (onInitialOpenPanelApplied) {
      onInitialOpenPanelApplied();
    }
  }, [fullscreen, initialOpenPanel, onInitialOpenPanelApplied]);

  const handleStartNewQuiz = async () => {
    if (!subjectId || !onGenerateNewQuiz) return;
    try {
      await onGenerateNewQuiz({
        subjectId: Number(subjectId),
        selectedQuizSource: quizSource,
        selectedQuestionCount: Number(questionCount),
        selectedSectionIds: selectedSectionIds
      });
      setOpenPanel(null);
    } catch (error) {
      // Error is shown by parent.
    }
  };

  const resultByQuestionId = React.useMemo(() => {
    const map = {};
    (result?.question_results || []).forEach((item) => {
      map[String(item.question_id)] = item;
    });
    return map;
  }, [result]);

  const reviewResultByQuestionId = React.useMemo(() => {
    const map = {};
    (reviewModalData?.question_results || []).forEach((item) => {
      map[String(item.question_id)] = item;
    });
    return map;
  }, [reviewModalData]);

  const handleOpenReviewModal = async (attemptId) => {
    if (!onOpenQuizHistoryAttempt || !attemptId) return;
    try {
      setOpeningHistoryAttemptId(attemptId);
      const details = await onOpenQuizHistoryAttempt(attemptId);
      if (details) {
        setOpenPanel(null);
        setReviewModalData(details);
      }
    } finally {
      setOpeningHistoryAttemptId(null);
    }
  };

  const handleOpenActiveQuizFromPanel = async (quizId) => {
    if (!onOpenActiveQuiz || !quizId) return;
    const loadedQuiz = await onOpenActiveQuiz(quizId);
    if (loadedQuiz) {
      setOpenPanel(null);
    }
  };

  const getCorrectAnswerText = (question, questionResult) => {
    const questionType = getQuestionType(question);
    if (!questionResult) return '';

    if (questionType === 'single_choice') {
      const idx = questionResult.correct_answer;
      if (Number.isInteger(idx) && Array.isArray(question.options) && question.options[idx] !== undefined) {
        return question.options[idx];
      }
      return String(idx ?? '');
    }

    if (questionType === 'multiple_choice') {
      const indexes = Array.isArray(questionResult.correct_answers) ? questionResult.correct_answers : [];
      const labels = indexes
        .map((idx) => (Array.isArray(question.options) && question.options[idx] !== undefined ? question.options[idx] : null))
        .filter(Boolean);
      return labels.join(', ') || t('quizCorrectAnswerMissing', userLanguage);
    }

    const accepted = Array.isArray(questionResult.acceptable_answers) ? questionResult.acceptable_answers : [];
    return accepted.join(', ') || t('quizCorrectAnswerMissing', userLanguage);
  };

  const getSelectedAnswerText = (question, selectedAnswer) => {
    const questionType = getQuestionType(question);
    if (selectedAnswer === null || selectedAnswer === undefined || selectedAnswer === '') {
      return t('quizNoAnswerGiven', userLanguage);
    }
    if (questionType === 'single_choice') {
      if (
        Number.isInteger(selectedAnswer) &&
        Array.isArray(question.options) &&
        question.options[selectedAnswer] !== undefined
      ) {
        return question.options[selectedAnswer];
      }
      return String(selectedAnswer);
    }
    if (questionType === 'multiple_choice') {
      if (!Array.isArray(selectedAnswer) || !Array.isArray(question.options)) return String(selectedAnswer);
      const labels = selectedAnswer
        .map((idx) => (question.options[idx] !== undefined ? question.options[idx] : null))
        .filter(Boolean);
      return labels.join(', ') || t('quizNoAnswerGiven', userLanguage);
    }
    return String(selectedAnswer);
  };

  const renderActiveQuizzesPanel = () => (
    <div className="quiz-history-card quiz-panel-card">
      <h3>{t('quizActiveTitle', userLanguage)}</h3>
      {loadingActiveQuizzes ? (
        <p>{t('loading', userLanguage)}</p>
      ) : activeQuizzes.length === 0 ? (
        <p>{t('quizActiveEmpty', userLanguage)}</p>
      ) : (
        <div className="quiz-history-list">
          {activeQuizzes.map((item) => (
            <button
              type="button"
              key={`${item.quiz_id}-${item.created_at}`}
              className="quiz-history-item"
              onClick={() => handleOpenActiveQuizFromPanel(item.quiz_id)}
              disabled={openingActiveQuiz || generatingQuiz || loading}
              title="Click to open"
            >
              <div>
                <strong>{item.quiz_title}</strong>
                <p>{item.subject}</p>
              </div>
              <div className="quiz-history-score">
                <strong>{item.question_count} {t('quizQuestions', userLanguage)}</strong>
                {openingActiveQuiz ? <span>{t('quizOpeningActive', userLanguage)}</span> : null}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderHistoryPanel = () => (
    <div className="quiz-history-card quiz-panel-card">
      <h3>{t('quizHistoryTitle', userLanguage)}</h3>
      {loadingQuizHistory ? (
        <p>{t('loading', userLanguage)}</p>
      ) : completedQuizzes.length === 0 ? (
        <p>{t('quizHistoryEmpty', userLanguage)}</p>
      ) : (
        <div className="quiz-history-list">
          {completedQuizzes.map((item) => (
            <button
              type="button"
              key={`${item.attempt_id}-${item.completed_at}`}
              className="quiz-history-item"
              onClick={() => handleOpenReviewModal(item.attempt_id)}
              disabled={loadingQuizReview}
            >
              <div>
                <strong>{item.quiz_title}</strong>
                <p>{item.subject}</p>
              </div>
              <div className="quiz-history-score">
                {openingHistoryAttemptId === item.attempt_id ? (
                  <strong>{t('quizLoadingReview', userLanguage)}</strong>
                ) : (
                  <>
                    <strong>{item.score}/{item.total_questions}</strong>
                    <span>{item.score_percent}%</span>
                  </>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderSetupPanel = () => (
    <div className="quiz-new-card quiz-panel-card">
      <h3>{t('quizStartNewTitle', userLanguage)}</h3>
      <div className="quiz-new-grid">
        <div className="form-group quiz-config-group">
          <label htmlFor="quiz-tab-subject">{t('quizSubjectLabel', userLanguage)}</label>
          <select
            id="quiz-tab-subject"
            className="quiz-config-select"
            value={subjectId}
            onChange={(e) => {
              setSubjectId(e.target.value);
              setSelectedSectionIds([]);
              setAvailableSections([]);
            }}
            disabled={generatingQuiz}
          >
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group quiz-config-group">
          <label htmlFor="quiz-tab-source">{t('quizSourceLabel', userLanguage)}</label>
          <select
            id="quiz-tab-source"
            className="quiz-config-select"
            value={quizSource}
            onChange={(e) => setQuizSource(e.target.value)}
            disabled={generatingQuiz}
          >
            <option value="auto">{t('quizSourceAuto', userLanguage)}</option>
            <option value="weak_topics">{t('quizSourceWeakTopics', userLanguage)}</option>
            <option value="knowledge_topics">{t('quizSourceKnowledgeTopics', userLanguage)}</option>
            <option value="today_sections">{t('quizSourceTodaySections', userLanguage)}</option>
          </select>
        </div>
        <div className="form-group quiz-config-group">
          <label htmlFor="quiz-tab-count">{t('quizQuestionCountLabel', userLanguage)}</label>
          <select
            id="quiz-tab-count"
            className="quiz-config-select"
            value={questionCount}
            onChange={(e) => setQuestionCount(Number(e.target.value))}
            disabled={generatingQuiz}
          >
            {[3, 4, 5, 6, 7, 8, 9, 10].map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
        </div>
      </div>

      {quizSource === 'today_sections' && (
        <div className="form-group quiz-config-group">
          <label>{t('quizSectionsLabel', userLanguage)}</label>
          {loadingSections ? (
            <p>{t('loading', userLanguage)}</p>
          ) : availableSections.length === 0 ? (
            <div className="quiz-empty-sections-state">
              <p>{t('noSectionsFound', userLanguage)}</p>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  if (onAddSectionForSubject && subjectId) {
                    onAddSectionForSubject(Number(subjectId));
                  }
                }}
              >
                {t('addTodaysSection', userLanguage)}
              </button>
            </div>
          ) : (
            <div className="quiz-sections-container">
              {availableSections.map((section) => (
                <button
                  type="button"
                  key={section.id}
                  className={`quiz-section-item-btn ${selectedSectionIds.includes(section.id) ? 'selected' : ''}`}
                  onClick={() => {
                    const isSelected = selectedSectionIds.includes(section.id);
                    setSelectedSectionIds((prev) => (
                      isSelected
                        ? prev.filter((id) => id !== section.id)
                        : [...prev, section.id]
                    ));
                  }}
                  aria-pressed={selectedSectionIds.includes(section.id)}
                  disabled={generatingQuiz}
                >
                  <span>
                    <strong>{section.topic}</strong>
                    <br />
                    <small>{(section.section_content || '').substring(0, 80)}...</small>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="quiz-submit-row">
        <button className="btn" onClick={handleStartNewQuiz} disabled={generatingQuiz || !subjectId}>
          {generatingQuiz
            ? t('loading', userLanguage)
            : t('quizStartNewAction', userLanguage)}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`quiz-page ${fullscreen ? 'quiz-page-fullscreen' : ''}`}>
      {fullscreen && (
        <div className="quiz-fullscreen-header">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
          >
            {t('backToSubjects', userLanguage)}
          </button>
          {!showFullscreenGenerating && (
            <div className="quiz-fullscreen-actions">
              <button
                type="button"
                className="btn-secondary quiz-setup-toggle-btn"
                onClick={() => setOpenPanel('active')}
              >
                {t('quizActiveTitle', userLanguage)}
              </button>
              <button
                type="button"
                className="btn-secondary quiz-setup-toggle-btn"
                onClick={() => setOpenPanel('setup')}
              >
                {t('quizStartNewTitle', userLanguage)}
              </button>
              <button
                type="button"
                className="btn-secondary quiz-setup-toggle-btn"
                onClick={() => setOpenPanel('history')}
              >
                {t('quizHistoryTitle', userLanguage)}
              </button>
            </div>
          )}
        </div>
      )}
      {showFullscreenGenerating && (
        <div className="quiz-generating-state">
          <h2>{t('quizGeneratingTitle', userLanguage)}</h2>
          <p>{t('quizGeneratingDescription', userLanguage)}</p>
          <div className="quiz-loading-bar">
            <div className="quiz-loading-bar-progress" />
          </div>
        </div>
      )}
      {!showFullscreenGenerating && (
        <>
      {!fullscreen && (
      <div className="quiz-panel-buttons-row">
        <button
          type="button"
          className="btn-secondary quiz-setup-toggle-btn"
          onClick={() => setOpenPanel('setup')}
        >
          {t('quizStartNewTitle', userLanguage)}
        </button>
        <button
          type="button"
          className="btn-secondary quiz-setup-toggle-btn"
          onClick={() => setOpenPanel('active')}
        >
          {t('quizActiveTitle', userLanguage)}
        </button>
        <button
          type="button"
          className="btn-secondary quiz-setup-toggle-btn"
          onClick={() => setOpenPanel('history')}
        >
          {t('quizHistoryTitle', userLanguage)}
        </button>
      </div>
      )}

      {quiz && (
        <>
          <div className="quiz-page-header">
            <div>
              <h2>{quiz.title}</h2>
              <p>{quiz.subject} • {quiz.questions?.length || 0} {t('quizQuestions', userLanguage)}</p>
            </div>
            {!fullscreen && onCancelQuiz && (
              <button
                type="button"
                className="btn-secondary"
                onClick={onCancelQuiz}
                disabled={loading}
              >
                {t('quizHideActive', userLanguage)}
              </button>
            )}
          </div>

          {result && (
            <div className="quiz-result-card">
              <h3>{t('quizResultTitle', userLanguage)}</h3>
              <p className="quiz-result-score">
                {result.score}/{result.total_questions}
              </p>
              <p className="quiz-result-percent">
                {result.score_percent}% {t('quizScoreLabel', userLanguage)}
              </p>
              {result.topics_to_practice?.length > 0 && (
                <p>
                  {t('quizTopicsToPractice', userLanguage)}: {result.topics_to_practice.join(', ')}
                </p>
              )}
            </div>
          )}

          <div className="quiz-questions-list">
            {(quiz.questions || []).map((question, idx) => {
          const questionType = getQuestionType(question);
          const answer = answers[String(question.id)];
          const questionResult = resultByQuestionId[String(question.id)];

          return (
            <div key={question.id} className="quiz-question-card">
              <h4>{idx + 1}. {question.question_text}</h4>
              {questionResult && (
                <div className={`quiz-question-status ${questionResult.is_correct ? 'correct' : 'incorrect'}`}>
                  {questionResult.is_correct
                    ? t('quizStatusCorrect', userLanguage)
                    : t('quizStatusIncorrect', userLanguage)}
                  {!questionResult.is_correct && (
                    <span className="quiz-correct-answer">
                      {' '}• {t('quizCorrectAnswerLabel', userLanguage)}: {getCorrectAnswerText(question, questionResult)}
                    </span>
                  )}
                </div>
              )}

              {questionType === 'single_choice' && Array.isArray(question.options) && (
                <div className="quiz-options-list">
                  {question.options.map((option, optionIdx) => (
                    <label key={optionIdx} className="quiz-option-item">
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        checked={answer === optionIdx}
                        onChange={() => onAnswerChange(question.id, optionIdx)}
                        disabled={loading || !!result}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {questionType === 'multiple_choice' && Array.isArray(question.options) && (
                <div className="quiz-options-list">
                  {question.options.map((option, optionIdx) => (
                    <label key={optionIdx} className="quiz-option-item">
                      <input
                        type="checkbox"
                        checked={Array.isArray(answer) && answer.includes(optionIdx)}
                        onChange={(e) => {
                          const prev = Array.isArray(answer) ? answer : [];
                          const next = e.target.checked
                            ? [...prev, optionIdx]
                            : prev.filter((item) => item !== optionIdx);
                          onAnswerChange(question.id, next);
                        }}
                        disabled={loading || !!result}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {questionType === 'text' && (
                <textarea
                  className="quiz-text-answer"
                  value={typeof answer === 'string' ? answer : ''}
                  onChange={(e) => onAnswerChange(question.id, e.target.value)}
                  placeholder={t('quizTextAnswerPlaceholder', userLanguage)}
                  disabled={loading || !!result}
                />
              )}
            </div>
          );
        })}
          </div>

          {!result && (
            <div className="quiz-submit-row quiz-submit-actions">
              <button
                className="btn-secondary quiz-cancel-btn"
                onClick={onCancelQuiz}
                disabled={loading}
              >
                {t('cancelQuiz', userLanguage)}
              </button>
              <button
                className="btn quiz-action-btn"
                onClick={onSubmit}
                disabled={loading}
              >
                {loading ? t('loading', userLanguage) : t('submitQuiz', userLanguage)}
              </button>
            </div>
          )}
        </>
      )}

      {reviewModalData && (
        <div className="modal-overlay" onClick={() => setReviewModalData(null)}>
          <div className="modal-content quiz-review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{reviewModalData.quiz?.title || t('quizResultTitle', userLanguage)}</h3>
              <button
                type="button"
                className="modal-close-btn"
                aria-label={t('closeReviewModal', userLanguage)}
                onClick={() => setReviewModalData(null)}
              >
                ×
              </button>
            </div>
            <div className="quiz-result-card">
              <p className="quiz-result-score">
                {reviewModalData.score}/{reviewModalData.total_questions}
              </p>
              <p className="quiz-result-percent">
                {reviewModalData.score_percent}% {t('quizScoreLabel', userLanguage)}
              </p>
            </div>

            <div className="quiz-questions-list">
              {(reviewModalData.quiz?.questions || []).map((question, idx) => {
                const questionResult = reviewResultByQuestionId[String(question.id)];
                const selectedAnswer = reviewModalData.answers?.[String(question.id)];
                return (
                  <div key={question.id} className="quiz-question-card">
                    <h4>{idx + 1}. {question.question_text}</h4>
                    <div className={`quiz-question-status ${questionResult?.is_correct ? 'correct' : 'incorrect'}`}>
                      {questionResult?.is_correct
                        ? t('quizStatusCorrect', userLanguage)
                        : t('quizStatusIncorrect', userLanguage)}
                    </div>
                    <p><strong>{t('quizYourAnswerLabel', userLanguage)}:</strong> {getSelectedAnswerText(question, selectedAnswer)}</p>
                    <p><strong>{t('quizCorrectAnswerLabel', userLanguage)}:</strong> {getCorrectAnswerText(question, questionResult)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {openPanel && (
        <div className="modal-overlay" onClick={() => setOpenPanel(null)}>
          <div className="modal-content quiz-panel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {openPanel === 'setup' && t('quizStartNewTitle', userLanguage)}
                {openPanel === 'active' && t('quizActiveTitle', userLanguage)}
                {openPanel === 'history' && t('quizHistoryTitle', userLanguage)}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setOpenPanel(null)}
                aria-label={t('closeReviewModal', userLanguage)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {openPanel === 'setup' && renderSetupPanel()}
              {openPanel === 'active' && renderActiveQuizzesPanel()}
              {openPanel === 'history' && renderHistoryPanel()}
            </div>
          </div>
        </div>
      )}
      </>
      )}

    </div>
  );
};

export default QuizTab;
